import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { useCognitoAuth } from './CognitoAuthContext';
import { getUserIdAsNumber, coerceAppBusinessArea } from '../utils/cognitoUtils';
import apiService from '../services/api';

export type BusinessArea = 'telecommunications' | 'energy_solutions';

interface AppContextType {
  currentBusinessArea: BusinessArea;
  setCurrentBusinessArea: (area: BusinessArea) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  notifications: any[];
  addNotification: (notification: any) => void;
  clearNotifications: () => void;
  markNotificationAsRead: (id: string) => void;
  fetchNotifications: () => Promise<void>;
  loading: boolean;
  refreshCalendarData: () => void;
  onCalendarDataRefresh: (callback: () => void) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { user } = useCognitoAuth();
  const [currentBusinessArea, setCurrentBusinessArea] = useState<BusinessArea>('telecommunications');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const hasInitialized = useRef(false);
  const calendarRefreshCallbacks = useRef<(() => void)[]>([]);

  // Set initial business area once when user loads (ignore invalid DB values like legacy role strings)
  useEffect(() => {
    if (!user || hasInitialized.current) return;
    setCurrentBusinessArea(coerceAppBusinessArea(user.business_area));
    hasInitialized.current = true;
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;
    
    // Don't fetch notifications if user is in partnership mode
    const userTenant = localStorage.getItem('user_tenant');
    if (userTenant === 'partnership') {
      return;
    }
    
    setLoading(true);
    try {
      const data = await apiService.getNotifications(getUserIdAsNumber(user));
      setNotifications(data || []);
    } catch (error) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications when user changes
  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setNotifications([]);
      // Reset initialization flag when user logs out
      hasInitialized.current = false;
    }
  }, [user]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [user]);

  const addNotification = (notification: any) => {
    setNotifications(prev => [notification, ...prev]);
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const markNotificationAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const refreshCalendarData = () => {
    calendarRefreshCallbacks.current.forEach(callback => callback());
  };

  const onCalendarDataRefresh = (callback: () => void) => {
    calendarRefreshCallbacks.current.push(callback);
    return () => {
      const index = calendarRefreshCallbacks.current.indexOf(callback);
      if (index > -1) {
        calendarRefreshCallbacks.current.splice(index, 1);
      }
    };
  };

  const value: AppContextType = {
    currentBusinessArea,
    setCurrentBusinessArea,
    sidebarCollapsed,
    setSidebarCollapsed,
    notifications,
    addNotification,
    clearNotifications,
    markNotificationAsRead,
    fetchNotifications,
    loading,
    refreshCalendarData,
    onCalendarDataRefresh,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}; 